/**
 * Client-Side Logger
 *
 * A simple logger for client-side code that:
 * - Only logs in development mode
 * - Provides structured logging with categories
 * - Can be easily disabled in production
 *
 * Usage:
 * ```typescript
 * import { clientLogger } from '#app/utils/pz/logger/clientLogger';
 *
 * // Simple logging
 * clientLogger.debug('Component rendered');
 * clientLogger.info('User clicked button');
 * clientLogger.warn('Deprecated prop used');
 * clientLogger.error('Failed to fetch data', error);
 *
 * // With category
 * const log = clientLogger.scoped('DialogWizard');
 * log.debug('Step changed', { from: 1, to: 2 });
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogMetadata {
  [key: string]: unknown;
}

const isDevelopment = typeof process !== 'undefined' 
  ? process.env.NODE_ENV === 'development'
  : typeof window !== 'undefined' && (window as any).__DEV__;

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Default minimum log level (can be overridden via localStorage in browser)
const getMinLogLevel = (): LogLevel => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('LOG_LEVEL');
    if (stored && LOG_LEVEL_PRIORITY[stored as LogLevel] !== undefined) {
      return stored as LogLevel;
    }
  }
  return 'debug';
};

const shouldLog = (level: LogLevel): boolean => {
  if (!isDevelopment) return false;
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[getMinLogLevel()];
};

const formatMessage = (
  level: LogLevel,
  scope: string | undefined,
  message: string,
  metadata?: LogMetadata
): string => {
  const prefix = scope ? `[${scope}]` : '';
  const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : '';
  return `${prefix} ${message}${metaStr}`;
};

/**
 * Log a debug message (only in development)
 */
const debug = (message: string, metadata?: LogMetadata): void => {
  if (shouldLog('debug')) {
    console.debug(formatMessage('debug', undefined, message, metadata));
  }
};

/**
 * Log an info message (only in development)
 */
const info = (message: string, metadata?: LogMetadata): void => {
  if (shouldLog('info')) {
    console.log(formatMessage('info', undefined, message, metadata));
  }
};

/**
 * Log a warning message (only in development)
 */
const warn = (message: string, metadata?: LogMetadata): void => {
  if (shouldLog('warn')) {
    console.warn(formatMessage('warn', undefined, message, metadata));
  }
};

/**
 * Log an error message (only in development)
 */
const error = (message: string, errorOrMetadata?: Error | LogMetadata): void => {
  if (shouldLog('error')) {
    if (errorOrMetadata instanceof Error) {
      console.error(formatMessage('error', undefined, message), errorOrMetadata);
    } else {
      console.error(formatMessage('error', undefined, message, errorOrMetadata));
    }
  }
};

/**
 * Create a scoped logger with a prefix
 *
 * @param scope - The scope/component name to prefix logs with
 * @returns A logger with all methods prefixed with the scope
 *
 * @example
 * ```typescript
 * const log = clientLogger.scoped('DialogWizard');
 * log.debug('Rendering', { step: 1 });
 * // Output: [DialogWizard] Rendering {"step":1}
 * ```
 */
const scoped = (scope: string) => ({
  debug: (message: string, metadata?: LogMetadata): void => {
    if (shouldLog('debug')) {
      console.debug(formatMessage('debug', scope, message, metadata));
    }
  },
  info: (message: string, metadata?: LogMetadata): void => {
    if (shouldLog('info')) {
      console.log(formatMessage('info', scope, message, metadata));
    }
  },
  warn: (message: string, metadata?: LogMetadata): void => {
    if (shouldLog('warn')) {
      console.warn(formatMessage('warn', scope, message, metadata));
    }
  },
  error: (message: string, errorOrMetadata?: Error | LogMetadata): void => {
    if (shouldLog('error')) {
      if (errorOrMetadata instanceof Error) {
        console.error(formatMessage('error', scope, message), errorOrMetadata);
      } else {
        console.error(formatMessage('error', scope, message, errorOrMetadata));
      }
    }
  },
});

/**
 * Client-side logger that only logs in development mode
 */
export const clientLogger = {
  debug,
  info,
  warn,
  error,
  scoped,
};

/**
 * Helper to check if we're in development mode
 */
export const isDevMode = isDevelopment;

/**
 * Set the minimum log level (persisted to localStorage)
 *
 * @param level - Minimum log level to show
 */
export const setLogLevel = (level: LogLevel): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('LOG_LEVEL', level);
  }
};
